import { Link, useSearchParams } from 'react-router';
import { useInfiniteQuery } from '@tanstack/react-query';
import type { DealList, DealStatus, DealSummary } from '@agency/contracts';
import { Badge, Button, EmptyState, Skeleton } from '@agency/ui';
import { ApiClientError, get } from '../../lib/api.ts';
import { useAuth } from '../../lib/auth.tsx';
import { fmtDate, RESTRICTED } from '../../lib/format.ts';
import { QueryError } from '../../lib/query-state.tsx';
import { DealDrawer } from './deal-drawer.tsx';

const VIEWS: { status?: DealStatus; label: string }[] = [
  { label: 'All deals' },
  { status: 'PROPOSAL', label: 'Proposal' },
  { status: 'WON', label: 'Won' },
  { status: 'LOST', label: 'Lost' },
];

function DealRow({ deal, canOpenLead, onOpen }: { deal: DealSummary; canOpenLead: boolean; onOpen: () => void }) {
  const title = deal.piiRedacted ? RESTRICTED : deal.title;
  return (
    <tr>
      <td data-col="deal">
        <span className="ag-cell-2">
          {canOpenLead ? <Link to={`/leads?${new URLSearchParams({ lead: deal.leadId })}`}>{title}</Link> : <strong>{title}</strong>}
          <span>{deal.piiRedacted ? 'Contact details hidden for your role' : [deal.contactName, deal.contactEmail].filter(Boolean).join(' · ') || 'No contact recorded'}</span>
        </span>
      </td>
      <td data-col="stage"><Badge tone={deal.status === 'WON' ? 'success' : deal.status === 'PROPOSAL' ? 'accent' : 'neutral'}>{deal.status === 'PROPOSAL' ? deal.stageName : deal.status === 'WON' ? 'Won' : 'Lost'}</Badge></td>
      <td data-col="value" className="ag-num">{deal.amount} {deal.currency}</td>
      <td data-col="probability" className="ag-num">{deal.status === 'PROPOSAL' ? `${deal.probability}%` : '—'}</td>
      <td data-col="close">{deal.expectedCloseDate ?? '—'}</td>
      <td data-col="owner">{deal.ownerName ?? 'Unassigned'}</td>
      <td data-col="landing"><span className="ag-cell-2"><span>{deal.landingName}</span><span>v{deal.landingVersionNumber}</span></span></td>
      <td data-col="proposed" title={deal.proposedAt}>{fmtDate(deal.proposedAt)}</td>
      <td data-col="won">{deal.wonAt ? fmtDate(deal.wonAt) : '—'}</td>
      <td data-col="details"><Button size="sm" onClick={onOpen}>Details</Button></td>
    </tr>
  );
}

export function DealsPage() {
  const [params, setParams] = useSearchParams();
  const { can } = useAuth();
  const status = VIEWS.find((view) => view.status === params.get('status'))?.status;
  const selected = params.get('deal');
  const setSelected = (id: string | null) => setParams((current) => { const next = new URLSearchParams(current); id ? next.set('deal', id) : next.delete('deal'); return next; });
  const query = useInfiniteQuery<DealList, ApiClientError>({
    queryKey: ['deals', status ?? 'all'],
    initialPageParam: '',
    queryFn: ({ pageParam }) => {
      const search = new URLSearchParams({ limit: '25' });
      if (status) search.set('status', status);
      if (pageParam) search.set('cursor', String(pageParam));
      return get<DealList>(`/deals?${search}`);
    },
    getNextPageParam: (last) => last.nextCursor ?? undefined,
  });
  const items = query.data?.pages.flatMap((page) => page.items) ?? [];
  const counts = query.data?.pages[0]?.counts ?? {};
  const total = Object.values(counts).reduce((sum, count) => sum + count, 0);

  return (
    <>
      <div className="pagehead"><div><h1>Deals</h1><p>Proposal values and outcomes from your inquiry records. Amounts retain their original currencies.</p></div>{can('deals.manage') ? <Link to="/pipeline/stages" className="ag-btn ag-btn--secondary">Manage stages</Link> : null}</div>
      <div className="views" role="group" aria-label="Deal views">
        {VIEWS.map((view) => (
          <button key={view.status ?? 'all'} type="button" className="view" aria-pressed={status === view.status} onClick={() => setParams(view.status ? { status: view.status } : {})}>
            {view.label}<span className="view__n">{query.isSuccess ? view.status ? counts[view.status] ?? 0 : total : '–'}</span>
          </button>
        ))}
      </div>
      {query.isLoading ? (
        <div role="status"><span className="ag-visually-hidden">Loading deals…</span><Skeleton width="100%" height={180} /></div>
      ) : query.isError ? (
        <QueryError error={query.error} onRetry={() => void query.refetch()} />
      ) : items.length === 0 ? (
        <EmptyState title={status ? `No ${status.toLowerCase()} deals` : 'No deals yet'}>
          {status ? 'Choose another view to see deals at a different stage.' : 'A deal appears here when a qualified lead receives a proposal.'}
        </EmptyState>
      ) : (
        <>
          <div className="ag-table-wrap">
            <table className="ag-table deals-table">
              <caption className="ag-visually-hidden">Deals, newest proposals first</caption>
              <thead><tr><th scope="col">Deal</th><th scope="col">Stage</th><th scope="col">Value</th><th scope="col">Probability</th><th scope="col">Close date</th><th scope="col">Owner</th><th scope="col">Landing</th><th scope="col">Proposed</th><th scope="col">Won</th><th scope="col">Details</th></tr></thead>
              <tbody>{items.map((deal) => <DealRow key={deal.id} deal={deal} canOpenLead={can('leads.read')} onOpen={() => setSelected(deal.id)} />)}</tbody>
            </table>
          </div>
          <div className="pager"><span aria-live="polite">Showing {items.length} of {status ? counts[status] ?? 0 : total} deal{total === 1 ? '' : 's'}</span>{query.hasNextPage ? <Button onClick={() => void query.fetchNextPage()} loading={query.isFetchingNextPage}>Load more</Button> : null}</div>
        </>
      )}
      <DealDrawer id={selected} onClose={() => setSelected(null)} />
    </>
  );
}
